// src/hooks/useReveal.js
import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Animates every `.reveal` inside the returned ref as it scrolls into view.
 *
 * Elements start hidden via the `.reveal` class in GlobalStyles, so there's no
 * flash of unstyled content. That also means anything this hook fails to pick
 * up stays permanently invisible — so two things matter:
 *
 *   1. `deps` — pass whatever changes when async content arrives (a product
 *      count, a loading flag). Cards fetched from Firestore mount long after
 *      the first effect ran, and without this they'd never be registered.
 *   2. the safety pass below — a last-resort unhide for anything still at
 *      zero opacity with no tween attached.
 *
 * Usage:
 *   const scope = useReveal({ deps: [products.length] });
 *   <Section ref={scope}><h2 className="reveal">…</h2></Section>
 */
export default function useReveal({
  y = 16,
  duration = 0.42,
  start = "top 88%",
  deps = [],
} = {}) {
  const scope = useRef(null);

  useLayoutEffect(() => {
    const el = scope.current;
    if (!el) return;

    const show = (nodes) => gsap.set(nodes, { opacity: 1, y: 0, scale: 1 });

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      show(el.querySelectorAll(".reveal"));
      return;
    }

    const ctx = gsap.context(() => {
      // grouped reveals: children of [data-reveal-stagger] animate together
      gsap.utils.toArray("[data-reveal-stagger]").forEach((group) => {
        const kids = gsap.utils
          .toArray(group.querySelectorAll(".reveal"))
          .filter((n) => !n.dataset.revealed);
        if (!kids.length) return;

        kids.forEach((n) => (n.dataset.revealed = "1"));

        gsap.fromTo(
          kids,
          { opacity: 0, y, scale: 0.96 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration,
            ease: "back.out(1.4)",
            stagger: { each: 0.06, from: "start", grid: "auto" },
            scrollTrigger: { trigger: group, start, once: true },
          },
        );
      });

      // standalone reveals
      gsap.utils.toArray(".reveal").forEach((node) => {
        if (node.closest("[data-reveal-stagger]")) return;
        if (node.dataset.revealed) return;
        node.dataset.revealed = "1";

        gsap.fromTo(
          node,
          { opacity: 0, y },
          {
            opacity: 1,
            y: 0,
            duration: duration * 1.6,
            delay: parseFloat(node.dataset.revealDelay) || 0,
            ease: "power3.out",
            scrollTrigger: { trigger: node, start, once: true },
          },
        );
      });

      ScrollTrigger.refresh();
    }, scope);

    // images finishing late shift the layout and stale triggers fire early
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener("load", refresh);

    /**
     * Safety pass. If anything is still invisible with no tween on it — a
     * trigger that never fired, a layout that settled oddly — show it.
     * An unanimated element is a minor loss; an invisible one is a broken page.
     */
    const safety = setTimeout(() => {
      if (!scope.current) return;
      scope.current.querySelectorAll(".reveal").forEach((node) => {
        const hidden = Number(getComputedStyle(node).opacity) < 0.05;
        if (hidden && !gsap.isTweening(node)) {
          gsap.to(node, { opacity: 1, y: 0, scale: 1, duration: 0.3 });
        }
      });
    }, 2200);

    return () => {
      window.removeEventListener("load", refresh);
      clearTimeout(safety);
      ctx.revert();
      // ctx.revert() restores inline styles, so clear the marks too —
      // otherwise a remount would skip every element as "already revealed".
      scope.current
        ?.querySelectorAll("[data-revealed]")
        .forEach((n) => delete n.dataset.revealed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [y, duration, start, ...deps]);

  return scope;
}
